import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { clientService } from '../services/clientService'
import { CreateClientData } from '../types'
import {
  UserPlusIcon,
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  MapPinIcon,
  GlobeAltIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon
} from '@heroicons/react/24/outline'

const AddClientPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [clientData, setClientData] = useState<CreateClientData & {
    address?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    website?: string
    instagram?: string
    referralSource?: string
    projectType?: string
    budget?: string
  }>({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    website: '',
    instagram: '',
    referralSource: '',
    projectType: '',
    budget: ''
  })
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setClientData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!firstName || !lastName || !clientData.email) {
      setError('First name, last name, and email are required')
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(clientData.email)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Combine all additional info into notes if provided
      const additionalInfo = []
      if (clientData.address) additionalInfo.push(`Address: ${clientData.address}`)
      if (clientData.city) additionalInfo.push(`City: ${clientData.city}`)
      if (clientData.state) additionalInfo.push(`State: ${clientData.state}`)
      if (clientData.zipCode) additionalInfo.push(`ZIP: ${clientData.zipCode}`)
      if (clientData.country) additionalInfo.push(`Country: ${clientData.country}`)
      if (clientData.website) additionalInfo.push(`Website: ${clientData.website}`)
      if (clientData.instagram) additionalInfo.push(`Instagram: ${clientData.instagram}`)
      if (clientData.referralSource) additionalInfo.push(`Referral: ${clientData.referralSource}`)
      if (clientData.projectType) additionalInfo.push(`Project Type: ${clientData.projectType}`)
      if (clientData.budget) additionalInfo.push(`Budget: ${clientData.budget}`)
      
      const combinedNotes = [clientData.notes, ...additionalInfo]
        .filter(Boolean)
        .join('\n')

      const finalClientData = {
        name: `${firstName} ${lastName}`,
        email: clientData.email,
        phone: clientData.phone,
        company: clientData.company,
        notes: combinedNotes
      }

      await clientService.createClient(finalClientData, user!.id)
      setSuccess(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (err: any) {
      if (err.message?.includes('duplicate key')) {
        setError('A client with this email already exists')
      } else {
        setError('Failed to create client. Please try again.')
      }
      console.error('Error creating client:', err)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Background orbs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="glass-mockup rounded-3xl p-12 text-center max-w-md"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Client Added!</h2>
          <p className="text-gray-400">The client has been successfully added to your database.</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="text-lg font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <UserPlusIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Add <span className="text-gray-400">Client</span>
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-3xl p-8"
        >
          <h1 className="text-3xl font-bold text-white mb-8">Add New Client</h1>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center space-x-3"
            >
              <XMarkIcon className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <UserIcon className="w-6 h-6" />
                <span>Basic Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-3">First Name *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Last Name *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Email Address *</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={clientData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Phone Number</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={clientData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Company/Organization</label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={clientData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="Acme Corporation"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <MapPinIcon className="w-6 h-6" />
                <span>Address Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-white font-medium mb-3">Street Address</label>
                  <input
                    type="text"
                    value={clientData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="123 Main Street"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">City</label>
                  <input
                    type="text"
                    value={clientData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="New York"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">State/Province</label>
                  <input
                    type="text"
                    value={clientData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="NY"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">ZIP/Postal Code</label>
                  <input
                    type="text"
                    value={clientData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="10001"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Country</label>
                  <input
                    type="text"
                    value={clientData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="United States"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Online Presence */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <GlobeAltIcon className="w-6 h-6" />
                <span>Online Presence</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-3">Website</label>
                  <input
                    type="url"
                    value={clientData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Instagram Handle</label>
                  <input
                    type="text"
                    value={clientData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    placeholder="@username"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Project Information */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <CameraIcon className="w-6 h-6" />
                <span>Project Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-3">Project Type</label>
                  <select
                    value={clientData.projectType}
                    onChange={(e) => handleInputChange('projectType', e.target.value)}
                    className="input-field"
                  >
                    <option value="" className="bg-gray-800">Select project type</option>
                    <option value="Wedding" className="bg-gray-800">Wedding</option>
                    <option value="Portrait" className="bg-gray-800">Portrait</option>
                    <option value="Corporate" className="bg-gray-800">Corporate</option>
                    <option value="Product" className="bg-gray-800">Product</option>
                    <option value="Event" className="bg-gray-800">Event</option>
                    <option value="Real Estate" className="bg-gray-800">Real Estate</option>
                    <option value="Fashion" className="bg-gray-800">Fashion</option>
                    <option value="Food" className="bg-gray-800">Food</option>
                    <option value="Video Production" className="bg-gray-800">Video Production</option>
                    <option value="Other" className="bg-gray-800">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-3">Budget Range</label>
                  <select
                    value={clientData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    className="input-field"
                  >
                    <option value="" className="bg-gray-800">Select budget range</option>
                    <option value="Under $500" className="bg-gray-800">Under $500</option>
                    <option value="$500 - $1,000" className="bg-gray-800">$500 - $1,000</option>
                    <option value="$1,000 - $2,500" className="bg-gray-800">$1,000 - $2,500</option>
                    <option value="$2,500 - $5,000" className="bg-gray-800">$2,500 - $5,000</option>
                    <option value="$5,000 - $10,000" className="bg-gray-800">$5,000 - $10,000</option>
                    <option value="$10,000+" className="bg-gray-800">$10,000+</option>
                    <option value="To be discussed" className="bg-gray-800">To be discussed</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-white font-medium mb-3">How did they find you?</label>
                  <select
                    value={clientData.referralSource}
                    onChange={(e) => handleInputChange('referralSource', e.target.value)}
                    className="input-field"
                  >
                    <option value="" className="bg-gray-800">Select referral source</option>
                    <option value="Google Search" className="bg-gray-800">Google Search</option>
                    <option value="Instagram" className="bg-gray-800">Instagram</option>
                    <option value="Facebook" className="bg-gray-800">Facebook</option>
                    <option value="Word of Mouth" className="bg-gray-800">Word of Mouth</option>
                    <option value="Previous Client" className="bg-gray-800">Previous Client</option>
                    <option value="Wedding Vendor" className="bg-gray-800">Wedding Vendor</option>
                    <option value="Website" className="bg-gray-800">Website</option>
                    <option value="Photography Forum" className="bg-gray-800">Photography Forum</option>
                    <option value="Other" className="bg-gray-800">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <DocumentTextIcon className="w-6 h-6" />
                <span>Additional Notes</span>
              </h2>
              
              <textarea
                value={clientData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional notes about the client, special requirements, preferences, etc."
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold text-lg hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Adding Client...' : 'Add Client'}
            </motion.button>
          </form>
        </motion.div>
      </main>
    </div>
  )
}

export default AddClientPage 